import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import API from 'api/API';
import { UploadType } from 'api/Responses/BulkUploadResponses';
import Button from 'components/Button';
import { useLanguage } from 'context/AppContext';
import { selectUnique, selectUpdate } from 'store/dataUpload/dataUploadSlice';
import DataUploadPageTemplate from '../molecules/DataUploadPageTemplate';
import EditDataUploadMerge from '../molecules/EditDataUploadMerge';
import ProceedWithCreateDraftsModal from '../molecules/ProceedWithCreateDraftsModal';
import WarningUnprocessedDuplicatesModal from '../molecules/WarningUnprocessedDuplicatesModal';
import * as Styles from './DataUploadMerge.styles';

export type DataUploadMergeParams = {
  uploadId: string;
  uploadType: UploadType;
};

const DataUploadMergePage: FC = () => {
  const navigate = useNavigate();
  const { uploadId = '', uploadType = UploadType['Device Upload'] } = useParams<DataUploadMergeParams>();
  const [proceedModalOpen, setProceedModalOpen] = useState(false);
  const uniqueArray = useSelector(selectUnique);
  const updateArray = useSelector(selectUpdate);
  const [warningUnprocessedDuplicatesModalOpen, setWarningUnprocessedDuplicatesModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [language] = useLanguage();

  const proceedButtonClick = (): void => {
    // check if any of the values in the 'update' tab have either not been ignored
    // or marked as 'Yes' for new draft
    const allDuplicatesProcessed = updateArray.some(item => !item.updated);
    // if the above is true, warn the user and inform them that they must process all elements
    if (allDuplicatesProcessed) {
      setWarningUnprocessedDuplicatesModalOpen(true);
    } else {
      setProceedModalOpen(true);
    }
  };

  const handleProceed = (): void => {
    (async () => {
      try {
        setIsLoading(true);
        // get a list of all of the items in the update items array that have been marked to override their duplicates in SADL so that
        // we can add these to the unique list to create the new drafts in the backend]
        if (uploadType === UploadType['Device Upload']) {
          const duplicateItemsToOverride = updateArray
            .filter(item => item.updated === true)
            .map(item => item?.updated_data?.device_id);
          const arrayIDs = uniqueArray.map(item => item.device_id);
          const itemIDsToCreateDrafts = [...duplicateItemsToOverride, ...arrayIDs].filter(Boolean) as string[];
          await API.bulkUpload.createPartDrafts(uploadId, { items: itemIDsToCreateDrafts });
        }
        if (uploadType === UploadType['Set Upload']) {
          const duplicateItemsToOverride = updateArray
            .filter(item => item.updated === true)
            .map(item => item?.updated_data?.set_code);
          const arrayIDs = uniqueArray.map(item => item.set_code);
          const itemIDsToCreateDrafts = [...duplicateItemsToOverride, ...arrayIDs].filter(Boolean) as string[];
          await API.bulkUpload.createSetDrafts(uploadId, { items: itemIDsToCreateDrafts });
        }
        if (uploadType === UploadType['Set Serial Numbers']) {
          const duplicateItemsToOverride = updateArray
            .filter(
              item => item.updated && item.updated_data && item.updated_data.set_code && item.updated_data.set_uss,
            )
            .map(item => ({
              set_code: item.updated_data?.set_code as string, // it's safe to assume that updated_data.set_code is not null here because we filter out items that don't have updated_data
              set_uss: item.updated_data?.set_uss as string,
              move_uss: true,
            }));
          const uniqueItemsToCreate = uniqueArray
            .filter(item => item.set_code && item.set_uss)
            .map(item => ({
              set_code: item.set_code as string,
              set_uss: item.set_uss as string,
              move_uss: false,
            }));
          const itemsToCreateSetSerialNumbers = [...duplicateItemsToOverride, ...uniqueItemsToCreate];

          await API.bulkUpload.createSetSerialNumbers(uploadId, { items: itemsToCreateSetSerialNumbers });
        }
        if (uploadType === UploadType['Device Attachments']) {
          const duplicateItemsToOverride = updateArray
            .filter(item => item.updated === true)
            .map(item => item?.file_path);
          const arrayIDs = uniqueArray.map(item => item.file_path);
          const itemIDsToCreateDrafts = [...duplicateItemsToOverride, ...arrayIDs].filter(Boolean) as string[];
          await API.bulkUpload.createPartAttachmentDrafts(uploadId, { items: itemIDsToCreateDrafts });
        }
        if (uploadType === UploadType['Set Attachments']) {
          const duplicateItemsToOverride = updateArray
            .filter(item => item.updated === true)
            .map(item => item?.file_path);
          const arrayIDs = uniqueArray.map(item => item.file_path);
          const itemIDsToCreateDrafts = [...duplicateItemsToOverride, ...arrayIDs].filter(Boolean) as string[];
          await API.bulkUpload.createSetAttachmentDrafts(uploadId, { items: itemIDsToCreateDrafts });
        }
        setIsLoading(false);
        toast.success(language.DataUpload.proceedWithCreateDraftsModal.success);
        setProceedModalOpen(false);
        navigate('/data-upload/list');
      } catch (error) {
        API.errorHandler(error);
        setIsLoading(false);
      }
    })();
  };

  return (
    <DataUploadPageTemplate>
      <Styles.DataUploadMergePage>
        <EditDataUploadMerge uploadId={uploadId} uploadType={uploadType} />
        <Styles.ActionBar>
          <Button onClick={() => navigate(-1)}>{language.DataUpload.proceedWithCreateDraftsModal.exit}</Button>
          <Button onClick={() => proceedButtonClick()}>
            {language.DataUpload.proceedWithCreateDraftsModal.proceed}
          </Button>
        </Styles.ActionBar>
      </Styles.DataUploadMergePage>
      <WarningUnprocessedDuplicatesModal
        open={warningUnprocessedDuplicatesModalOpen}
        close={() => setWarningUnprocessedDuplicatesModalOpen(false)}
      />
      <ProceedWithCreateDraftsModal
        open={proceedModalOpen}
        close={() => setProceedModalOpen(false)}
        handleProceed={handleProceed}
        isLoading={isLoading}
      />
    </DataUploadPageTemplate>
  );
};

export default DataUploadMergePage;
